import { useCallback, useEffect, useState } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { ApiError } from '../../api/client';
import { getMyStudios, type StudioDetail } from '../../api/partnerStudios';
import { BrandMark } from '../../components/shared/BrandMark';
import { loadAuthSession } from '../../data/authSession';
import '../../styles/partner.css';

export type PartnerOutletContext = {
  studios: StudioDetail[];
  studiosLoading: boolean;
  studiosError: string;
  /** 403 = 세션은 유효하나 OWNER 롤 아님 */
  ownerForbidden: boolean;
  reloadStudios: () => Promise<void>;
};

export function usePartnerContext() {
  return useOutletContext<PartnerOutletContext>();
}

const NAV_ITEMS: Array<{ label: string; to: string; end?: boolean }> = [
  { label: '대시보드', to: '/partner', end: true },
  { label: '예약 관리', to: '/partner/bookings' },
  { label: '스튜디오', to: '/partner/studios' },
  { label: '쿠폰', to: '/partner/coupons' },
  { label: '정산', to: '/partner/settlement' },
];

export function PartnerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = Boolean(loadAuthSession());

  const [studios, setStudios] = useState<StudioDetail[]>([]);
  const [studiosLoading, setStudiosLoading] = useState(true);
  const [studiosError, setStudiosError] = useState('');
  const [ownerForbidden, setOwnerForbidden] = useState(false);

  const reloadStudios = useCallback(async () => {
    try {
      const list = await getMyStudios();
      setStudios(list);
      setStudiosError('');
      setOwnerForbidden(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setOwnerForbidden(true);
        setStudiosError('');
        return;
      }
      setStudiosError(
        error instanceof Error && error.message
          ? error.message
          : '스튜디오 목록을 불러오지 못했습니다.',
      );
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    setStudiosLoading(true);
    reloadStudios().finally(() => {
      if (!cancelled) setStudiosLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, reloadStudios]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [isAuthenticated, location.pathname, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const context: PartnerOutletContext = {
    studios,
    studiosLoading,
    studiosError,
    ownerForbidden,
    reloadStudios,
  };

  return (
    <div className="partner-page">
      <header className="partner-header">
        <div className="partner-header__inner">
          <Link className="partner-header__brand" to="/partner">
            <BrandMark />
            <span className="partner-header__brand-badge">파트너</span>
          </Link>
          <nav aria-label="파트너 메뉴" className="partner-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                className={({ isActive }) =>
                  `partner-nav__link${isActive ? ' partner-nav__link--active' : ''}`
                }
                end={item.end}
                key={item.to}
                to={item.to}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="partner-header__spacer" />
          <Link className="partner-header__home-link" to="/">
            밴더 홈으로
          </Link>
        </div>
      </header>
      <main className="partner-main">
        {ownerForbidden ? (
          <div className="partner-card">
            <h2 className="partner-card__title">파트너 권한이 필요합니다</h2>
            <p className="partner-subtitle">
              점주(OWNER) 계정만 파트너 콘솔을 사용할 수 있습니다. 입점 신청 후 승인되면 이용할 수
              있어요.
            </p>
            <Link className="partner-btn" to="/business/apply">
              입점 신청하기
            </Link>
          </div>
        ) : (
          <Outlet context={context} />
        )}
      </main>
    </div>
  );
}
